import React, { useEffect, useRef } from 'react';
import { isEmpty } from 'lodash';
import { injectIntl } from 'react-intl';
import memoizeOne from 'memoize-one';
import classNames from 'classnames';

import { Circle, GoogleApiWrapper, InfoWindow, Map, Marker, Polygon } from '../Gmap';
import './MapContainer.scss';
import { Button, EButtonType } from '@repo/apg-ui';
import { formatNumber, reverseGeocode } from '../../utils/react';
import { removeHTMLTags } from '../../utils/string';
import { ReactComponent as Edit } from '../../assets/icons/edit_outline.svg';
import { ReactComponent as EditPlus } from '../../assets/icons/new/add_to_basket.svg';
import { ReactComponent as AddToBucket } from '../../assets/icons/new/basket_white.svg';
import { ReactComponent as RemoveIcon } from '../../assets/icons/new/trash.svg';
import { ReactComponent as Check } from '../../assets/icons/check.svg';
import { ReactComponent as RedX } from '../../assets/icons/redX.svg';
import { Dispatch } from 'redux';
import { MapResultPopup } from '../Modal/MapResultPopup';
import { Transition } from 'react-transition-group';
import ModalBackdrop from '../ModalBackdrop';
import { useNavigate } from 'react-router';
import parse from 'html-react-parser';
import { formatDate, getDateFromProduct } from '../../utils/product';

const placesAPIKey = process.env.REACT_APP_PLACES_API_KEY;

export enum MarkerColors {
  Red = '#B30200',
  Green = '#00C32C',
  Grey = '#817F7F',
  Yellow = '#d2a036',
}

interface Props {
  actionsJourney1?: any;
  actionsJourney2?: any;
  availableAttributes?: any;
  basketEntries?: Object;
  basketEntriesByBasketkey?: Object;
  center: any;
  criteriaLocations?: any;
  google: any;
  handleProductSelectClick?: any;
  intl: any;
  loading?: boolean;
  locations: any;
  modalMode?: boolean;
  overviewJ1?: boolean;
  overviewJ2?: boolean;
  productUrnStrBasket?: string;
  showingProductInfoWindow?: boolean;
  updateLatLngRad?: any;
  useCustomMarker?: boolean;
  isShowGreenUnavailable?: boolean;
  zoom?: number;
  dispatch?: Dispatch;
  updateProducts?: any;
}

const MapContainer = (props: Props) => {
  // const [circleRefs, setCircleRefs] = React.useState<any>({});
  const circleRefs = useRef([]);
  const infoWindowRefs = useRef([]);
  const [isMobile, setIsMobile] = React.useState<boolean>(false);
  const [showingInfoWindowModal, setShowingInfoWindowModal] = React.useState<boolean>(false);
  const [showingInfoWindow, setShowingInfoWindow] = React.useState<boolean>(false);
  const [showingPolygon, setShowingPolygon] = React.useState<string | boolean>(false);
  const [activeMarker, setActiveMarker] = React.useState<any>(null);
  const [circles, setCircles] = React.useState<any>({});
  const [mapPotentialCoords, setMapPotentialCoords] = React.useState<any>({});
  const navigate = useNavigate();

  const justOne = true;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 767) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
  }, []);

  const onMarkerShowPolygon = (props, marker, isShow = false) => {
    setShowingPolygon(isShow ? marker.name : isShow);
  };

  const onMarkerClick = (props, marker, e) => {
    setActiveMarker(marker);
    setShowingInfoWindow(marker.name);
    setShowingInfoWindowModal(marker.name);
  };

  const onInfoWindowClose = () => {
    setActiveMarker(null);
    setShowingInfoWindow(false);
    setShowingInfoWindowModal(false);
  };

  const onMapClicked = () => {
    if (showingInfoWindow) {
      setActiveMarker(null);
      setShowingInfoWindow(false);
      setShowingInfoWindowModal(false);
    }
  };

  const isAttributeChecked = (producttype, format, productshortcut) => {
    const { overviewJ1, overviewJ2, availableAttributes } = props;

    if (overviewJ2 || overviewJ1) {
      if (
        !producttype ||
        !format ||
        !(producttype in availableAttributes) ||
        !(format in availableAttributes[producttype])
      ) {
        return false;
      }

      let surfaceAttribute = 'Normalplakat';

      if (productshortcut === 'L') {
        surfaceAttribute = 'Leuchtend (L)';
      }
      if (productshortcut === 'LR') {
        surfaceAttribute = 'Leuchtend / Rotierend (LR)';
      }

      let availableForSurface = true;

      if ('surface' in availableAttributes) {
        availableForSurface = availableAttributes['surface'][surfaceAttribute];
      }

      return availableAttributes[producttype][format] && availableForSurface;
    } else {
      return true;
    }
  };

  const isBookedInPeriod = (product, productBasketentries): boolean => {
    const productDate = getDateFromProduct(product);
    return (
      (!!productBasketentries &&
        !!productDate &&
        productBasketentries.some(
          (entry) =>
            entry.basketstartdate &&
            productDate &&
            new Date(entry.basketstartdate) <= productDate[1] &&
            new Date(entry.basketenddate) >= productDate[0],
        )) ||
      (justOne && productBasketentries && productBasketentries.length > 0)
    );
  };

  const findProductInBasket = (product, productBasketentries): any => {
    const productDate = getDateFromProduct(product);
    return productBasketentries && !!productDate
      ? productBasketentries.find((entry: { basketstartdate: string; basketenddate: string; baskettimeslot: any }) => {
          return (
            entry.basketstartdate &&
            productDate &&
            (justOne ||
              (new Date(entry.basketstartdate) <= productDate[1] && new Date(entry.basketenddate) >= productDate[0]))
          );
        })
      : null;
  };

  const sumUpCosts = (productBasketentries): any => {
    let sum = 0;

    productBasketentries.map((entry, idx) => {
      if (entry.basketprice) {
        sum += entry.basketprice;
      }
    });
    return sum;
  };

  const handleClicks = (e) => {
    const { overviewJ2, basketEntries, handleProductSelectClick, locations } = props;

    const isProductBookedInPeriod = isBookedInPeriod(locations[activeMarker.name], basketEntries[activeMarker.name]);
    const bookedProduct = isProductBookedInPeriod
      ? findProductInBasket(locations[activeMarker.name], basketEntries[activeMarker.name])
      : null;
    const isDigiflex = bookedProduct && bookedProduct.producttype === 'digital';
    let id = e.srcElement.id;
    if (!id) {
      const findClosestButton = (element: any): any => {
        if (!element) return null;
        if (element.tagName && element.tagName.toLowerCase() === 'button') return element;
        return findClosestButton(element.parentElement);
      };
      const closestButton = findClosestButton(e.srcElement);
      id = closestButton ? closestButton.id : '';
    }
    if (id && id.includes('productView')) {
      navigate({
        search: `?modalContent=${isDigiflex ? 'digiflex' : 'analog'}&urn=${isProductBookedInPeriod ? bookedProduct.producturn : activeMarker.name}&basketkey=${isProductBookedInPeriod ? bookedProduct.basketkey : null}`,
        // search: `?modalContent=product&urn=${activeMarker.name}`,
      });
    } else if (id && id.includes('productSelect')) {
      if (overviewJ2) {
        handleProductSelectClick(
          bookedProduct ? bookedProduct : locations[activeMarker.name],
          !isProductBookedInPeriod,
        );
      } else {
        const val = !Boolean(locations[activeMarker.name].state === 0);
        handleProductSelectClick(locations[activeMarker.name], val);
      }
    }

    if (id === 'viewPotential') {
      props.updateProducts(mapPotentialCoords);
    }
  };

  const infoWindowOpened = (infoWindow) => {
    (document as any).querySelector('#infoWindowWrapper') &&
      (document as any).querySelector('#infoWindowWrapper').removeEventListener('click', handleClicks);
    (document as any).querySelector('#infoWindowWrapper') &&
      (document as any).querySelector('#infoWindowWrapper').addEventListener('click', handleClicks);
  };

  const getColorCustomMarker = (producturn: string | number) => {
    const { useCustomMarker, productUrnStrBasket, locations, isShowGreenUnavailable } = props;

    if (productUrnStrBasket && producturn && useCustomMarker && productUrnStrBasket.includes(String(producturn))) {
      return MarkerColors.Red;
    }

    if (
      locations[producturn] &&
      !isShowGreenUnavailable &&
      (locations[producturn].state !== 0 || (!locations[producturn].state && locations[producturn].state !== 0))
    ) {
      return MarkerColors.Grey;
    }

    if (
      locations[producturn] &&
      locations[producturn].area &&
      !isEmpty(locations[producturn].area) &&
      isEmpty(locations[producturn].subpoints)
    ) {
      return MarkerColors.Yellow;
    }

    return MarkerColors.Green;
  };

  const parseCityBorders = (data: string[]) => {
    const subPaths = [];

    if (!isEmpty(data)) {
      const returnCordsObj = ([lng, lat]) => {
        if (lng && lat) {
          return { lat, lng };
        }
      };

      const setCords = (item, i) => {
        subPaths[i] = [];
        item.forEach((item) => subPaths[i].push(returnCordsObj(item)));
      };

      data.forEach((item, i) => setCords(item, i));
    }

    return subPaths;
  };

  const getPaths = (producturn: string | number, locations: any, areaData: boolean = true) => {
    const area = locations[producturn] && locations[producturn].area ? locations[producturn].area : [];
    const subPoints = locations[producturn] && locations[producturn].subpoints ? locations[producturn].subpoints : [];
    const data = areaData ? area : subPoints;
    const paths = [];
    const subPaths = [];

    if (!isEmpty(data)) {
      const returnCordsObj = ([lng, lat]) => {
        if (lng && lat) {
          return { lat, lng };
        }
      };
      const returnCordsSubpoint = (item) => {
        if (item.x && item.y) {
          return { lat: item.y, lng: item.x };
        }
      };

      const setCords = (item, i) => {
        if (Array.isArray(item[0])) {
          subPaths[i] = [];
          item.forEach((item) => subPaths[i].push(returnCordsObj(item)));
        } else {
          if (item.hasOwnProperty('lng') && item.hasOwnProperty('ltd')) {
            paths.push(returnCordsObj(item));
          }
          if (item.hasOwnProperty('x') && item.hasOwnProperty('y')) {
            paths.push(returnCordsSubpoint(item));
          }
        }
      };

      data.forEach((item, i) => setCords(item, i));
    }

    return areaData && !isEmpty(subPaths) ? subPaths : paths;
  };

  const memoizedGetPaths = memoizeOne(getPaths);

  const isShowingInfoBlock = (name: any) => isMobile === false && activeMarker && showingInfoWindow === name;

  const updateLocationRadius = (location: any, lat: number, lng: number, radius: number) => {
    const { updateLatLngRad } = props;

    if (!!location && (location.lngLtd.lat != lat || location.lngLtd.lng != lng)) {
      reverseGeocode({ lat, lng })
        .then((res) => {
          const newAddress = res[0]['formatted_address'];
          const types = res[0]['types'];

          updateLatLngRad(lat, lng, radius, location.address, types, newAddress);
        })
        .catch((err) => console.error(err));
    } else if (location.circle.radius != radius) {
      updateLatLngRad(lat, lng, radius, location.address, location.types, null);
    }
  };

  const getTimeslotDescription = (product): string => {
    if (product.timeslotsinformation) {
      for (const timeslot of product.timeslotsinformation) {
        if (timeslot.timeslot === product.baskettimeslot) {
          return timeslot.abbr ? timeslot.abbr : timeslot.name;
        }
      }
    }
    return '';
  };

  const getZoom = (zoom) => (zoom ? zoom : window.innerWidth > 800 ? 11 : 14);
  const {
    basketEntries,
    center,
    criteriaLocations,
    google,
    intl,
    loading,
    locations,
    modalMode,
    overviewJ1,
    overviewJ2,
    showingProductInfoWindow,
    useCustomMarker,
    zoom,
  } = props;

  const cityBorders = [];

  if (criteriaLocations) {
    Object.keys(criteriaLocations).forEach((locationName, idx) => {
      if (criteriaLocations[locationName] && criteriaLocations[locationName].border) {
        const border = parseCityBorders(JSON.parse(criteriaLocations[locationName].border));
        cityBorders.push(border);
      }
    });
  }

  return (
    <div className='h-100'>
      {(overviewJ2 || overviewJ1) && (
        <>
          <Transition in={showingInfoWindowModal && isMobile} timeout={50}>
            {(state) => (
              <ModalBackdrop duration={250} state={state}>
                <MapResultPopup
                  isOpen={showingInfoWindowModal}
                  onClose={() => onInfoWindowClose()}
                  locations={locations}
                  intl={intl}
                  basketEntries={basketEntries}
                  overviewJ2={overviewJ2}
                  overviewJ1={overviewJ1}
                  loading={loading}
                  isBookedInPeriod={isBookedInPeriod}
                  findProductInBasket={findProductInBasket}
                  handleProductSelectClick={props.handleProductSelectClick}
                  isMarkerColorGrey={getColorCustomMarker(activeMarker?.name) === MarkerColors.Grey}
                  activeMarkerName={activeMarker?.name}
                />
              </ModalBackdrop>
            )}
          </Transition>
        </>
      )}
      {!isMobile && !modalMode && (
        <p>
          <b>
            {intl.formatMessage({
              id: 'ec.journey02.map.title',
              defaultMessage: 'Hinweis',
            })}
          </b>
          :&nbsp;
          {intl.formatMessage({
            id: 'ec.journey02.map.explanation',
            defaultMessage:
              'Auswahl/Änderungen von Mehrfachzeiträumen bei gleicher Fläche kann nur über Listview vorgenommen werden!',
          })}
        </p>
      )}
      <Map
        className='map'
        google={google}
        onClick={onMapClicked}
        zoom={getZoom(zoom)}
        onBoundsChanged={(mapProps, map, e) => {
          const bounds = map.getBounds();
          setMapPotentialCoords({
            'latitude-northeast': bounds.getNorthEast().lat(),
            'longitude-northeast': bounds.getNorthEast().lng(),
            'latitude-southwest': bounds.getSouthWest().lat(),
            'longitude-southwest': bounds.getSouthWest().lng(),
          });
        }}
        currentMarkerName={showingInfoWindow}
        center={center ? center : undefined}
        initialCenter={center ? center : undefined}
        style={{ width: '100%', height: '100' }}
      >
        {Object.keys(locations).map((keyAddress, idx) => {
          const producturn = locations[keyAddress].producturn;
          const productType = locations[keyAddress].formatcode;
          const format = locations[keyAddress].format;
          const address = locations[keyAddress].address;
          const mediacount = locations[keyAddress].mediacount;
          const producttype = locations[keyAddress].producttype;
          const productshortcut = locations[keyAddress].attributeshortcuts;
          const addressParsed = parse(String(address));
          const addressCleaned = removeHTMLTags(String(address));
          const paths = memoizedGetPaths(producturn, locations);
          const subPoints = memoizedGetPaths(producturn, locations, false);
          const dataSubpoints = locations[keyAddress].subpoints;
          const isExistPolygon = !isEmpty(paths);
          const isExistSubPoints = !isEmpty(subPoints);
          const markerColor = getColorCustomMarker(
            producturn || `${locations[keyAddress].lngLtd.lat}${locations[keyAddress].lngLtd.lng}`,
          );
          const isMarkerColorGrey = markerColor === MarkerColors.Grey;
          const locationProduct = locations[keyAddress].product;
          const checkId = String(address).toLowerCase().includes('id:');
          const mediaBlock =
            mediacount && mediacount > 1 ? (
              <p className='mt-3'>
                {`${mediacount} `}
                {intl.formatMessage({
                  id: 'ec.journey02.flachen',
                  defaultMessage: 'Flächen',
                })}
              </p>
            ) : null;

          const productBasketentries = basketEntries ? basketEntries[producturn] : null;
          const basketentry = findProductInBasket(locations[keyAddress], productBasketentries);
          const isProductBookedInPeriod = isBookedInPeriod(locations[keyAddress], productBasketentries);

          return (
            isAttributeChecked(producttype, format, productshortcut) && [
              isExistSubPoints && (!modalMode || !isExistPolygon)
                ? subPoints.map((marker, idx) => {
                    const dataOfSubpoint = dataSubpoints[idx];
                    if (marker && dataOfSubpoint) {
                      return (
                        <Marker
                          onClick={onMarkerClick}
                          key={`marker-${keyAddress}-${marker.lat}-${marker.lng}-${idx}`}
                          idx={idx}
                          title={dataOfSubpoint.name}
                          name={producturn || address}
                          nummer={dataOfSubpoint.number}
                          colorCustomMarker={markerColor}
                          productType={productType}
                          useCustomMarker={useCustomMarker}
                          position={marker}
                          image={dataOfSubpoint.image}
                          circle={null}
                          onMouseover={null}
                          onMouseout={null}
                        />
                      );
                    }

                    return null;
                  })
                : (!modalMode || (modalMode && (!isExistPolygon || (isExistPolygon && !isExistSubPoints)))) && (
                    <Marker
                      onClick={onMarkerClick}
                      key={keyAddress + 'marker' + idx}
                      idx={idx}
                      title={addressCleaned}
                      name={producturn || addressCleaned}
                      colorCustomMarker={markerColor}
                      productType={productType}
                      image={locationProduct?.productphotopath}
                      nummer={locationProduct?.productid}
                      useCustomMarker={useCustomMarker}
                      circle={circles[keyAddress]}
                      position={
                        overviewJ2 || overviewJ1
                          ? {
                              lat: locations[keyAddress].latitude,
                              lng: locations[keyAddress].longitude,
                            }
                          : locations[keyAddress].lngLtd
                      }
                      onMouseover={isExistPolygon ? (props, marker) => onMarkerShowPolygon(props, marker, true) : null}
                      onMouseout={isExistPolygon ? (props, marker) => onMarkerShowPolygon(props, marker) : null}
                    />
                  ),
              overviewJ2 || overviewJ1 ? (
                isShowingInfoBlock(producturn) && (
                  <InfoWindow
                    marker={activeMarker}
                    onOpen={infoWindowOpened}
                    onClose={() => onInfoWindowClose()}
                    key={`infoWindow${idx}`}
                    id={`infoWindow${idx}`}
                    ref={(ref) => {
                      infoWindowRefs.current[idx] = ref;
                    }}
                    google={google}
                    map={(window as any).google.maps}
                    visible={showingInfoWindow === producturn}
                  >
                    <div key={`windowContent${idx}`} id='infoWindowWrapper' className='info-popup'>
                      <div
                        className={classNames('col p-0 w-100', {
                          'product-image':
                            locations && locations[keyAddress] && !locations[keyAddress].productphotopath,
                        })}
                      >
                        <div className='product-img-wrapper'>
                          <img
                            alt='Product'
                            className='product-img w-100'
                            src={locations[keyAddress].productphotopath + '&resolution=thumbnail'}
                          />
                        </div>
                      </div>

                      <div className='p-16 d-flex flex-column justify-content-start product-info'>
                        <h4 className='w-100 fw-bold font-20 mb-3'>
                          {format}
                          {productshortcut}
                        </h4>

                        <div className='w-100 d-flex flex-row justify-content-start align-items-center'>
                          <div className='badge bg-light product-code'>
                            <span>ID: {locations[keyAddress].productid}</span>
                          </div>

                          {isMarkerColorGrey && (
                            <div className='d-flex justify-content-start align-items-center ms-4 availability'>
                              <span className='unavailable-checker ps-0'>
                                <RedX />
                              </span>
                              <span className='ps-0 font-14 font-600'>
                                {intl.formatMessage({
                                  id: 'ec.journey02.unavailable',
                                  defaultMessage: 'Nicht verfügbar',
                                })}
                              </span>
                            </div>
                          )}

                          {locations[keyAddress].shopstate === 0 && !isMarkerColorGrey && (
                            <div className='d-flex justify-content-start align-items-center ms-3 availability'>
                              <span className='green-checker ps-0'>
                                <Check />
                              </span>

                              <span className='ps-0 font-14 font-600'>
                                {intl.formatMessage({
                                  id: 'ec.journey02.available',
                                  defaultMessage: 'Verfügbar',
                                })}
                              </span>
                            </div>
                          )}
                        </div>

                        <p className='font-500 font-14 mt-3 address'>{addressParsed}</p>
                        {!!mediaBlock && mediaBlock}

                        <div className='d-flex justify-content-start mt-24'>
                          <span className='w-130 ps-0'>
                            <p className='mb-0 fw-bold'>
                              {intl.formatMessage({
                                id: 'ec.journey02.kostenCHF',
                                defaultMessage: 'Kosten CHF',
                              })}
                            </p>
                          </span>
                          <span>
                            <p className='fw-bold mb-0'>
                              {formatNumber(
                                isProductBookedInPeriod && basketentry
                                  ? sumUpCosts(basketEntries[producturn]) // basketentry.basketprice
                                  : locations[keyAddress].standardprice,
                                2,
                              )}
                            </p>
                          </span>
                        </div>

                        <div className='map-popup-productdatelist d-flex flex-column justify-content-start mt-3'>
                          {productBasketentries &&
                            productBasketentries.length > 0 &&
                            productBasketentries.map((item, idx) => (
                              <div className='d-flex flex-row justify-content-start' key={idx}>
                                <span>
                                  {item.producttype === 'digital' ? (
                                    <>
                                      {item.duration}
                                      {intl.formatMessage({ id: 'ec.general.days.abbreviation', defaultMessage: 'd' })}
                                      {item.baskettimeslot ? `, ${getTimeslotDescription(item)}` : ''}
                                      {item.basketspotlength ? `, ${item.basketspotlength}s` : ''},&nbsp;
                                    </>
                                  ) : (
                                    <>
                                      {item.duration}{' '}
                                      {intl.formatMessage({ id: 'ec.general.days', defaultMessage: 'Tage' })},&nbsp;
                                    </>
                                  )}
                                </span>
                                <span>
                                  {formatDate(item.basketstartdate, 'eee, dd.MM', intl.locale)} -{' '}
                                  {formatDate(item.basketenddate, '', intl.locale)}
                                </span>
                              </div>
                            ))}
                        </div>

                        {productBasketentries && productBasketentries.length > 1 && (
                          <p style={{ marginTop: '20px', color: 'red', lineHeight: '1.9rem' }}>
                            {intl.formatMessage({
                              id: 'ec.journey02.map.noEdit',
                              defaultMessage: 'Editieren von mehren Zeiträumen nur über Listansicht möglich',
                            })}
                          </p>
                        )}

                        {(!productBasketentries || productBasketentries.length < 2) && (
                          <div className='d-flex flex-row justify-content-start ms-0 mt-3'>
                            <Button
                              id={`productView${producturn}`}
                              buttonType={
                                isProductBookedInPeriod && !justOne
                                  ? EButtonType.PillMedium
                                  : EButtonType.InvertedPillMedium
                              }
                              className='map-product-button'
                            >
                              <div className='d-flex justify-content-center align-items-center h-100'>
                                {isProductBookedInPeriod && !justOne ? (
                                  <EditPlus className='edit-svg-icon' />
                                ) : (
                                  <Edit className='edit-svg-icon' />
                                )}
                              </div>
                            </Button>

                            <Button
                              className='font-600 map-product-button'
                              id={`productSelect${producturn}`}
                              buttonType={
                                isProductBookedInPeriod ? EButtonType.InvertedPillMedium : EButtonType.PillMedium
                              }
                              isLoading={loading}
                            >
                              {!isProductBookedInPeriod && (
                                <div className='d-flex justify-content-center align-items-center h-100'>
                                  <AddToBucket />
                                </div>
                              )}
                              {isProductBookedInPeriod && (
                                <div className='remove-action'>
                                  <RemoveIcon />
                                </div>
                              )}
                            </Button>
                          </div>
                        )}
                        {/*<Button id="viewPotential">Search area for advertising surfaces</Button>*/}
                      </div>
                    </div>
                  </InfoWindow>
                )
              ) : showingProductInfoWindow && isShowingInfoBlock(address) && checkId ? (
                <InfoWindow
                  marker={activeMarker}
                  onOpen={infoWindowOpened}
                  onClose={() => onInfoWindowClose()}
                  key={`infoWindow${idx}`}
                  id={`infoWindow${idx}`}
                  ref={(ref) => {
                    infoWindowRefs.current[idx] = ref;
                  }}
                  google={google}
                  map={(window as any).google.maps}
                  visible={checkId}
                >
                  {/* J2 step1 */}
                  <p className='fw-bold'>{locationProduct.format}</p>
                  <div className='product-image col-6 ps-0'>
                    <div className='product-img-wrapper'>
                      <div className='code-product'>{locationProduct.mediainfo.nummer}</div>
                      <img
                        alt='Product'
                        className='product-image'
                        src={locationProduct.productphotopath + '&resolution=thumbnail'}
                      />
                    </div>
                  </div>
                  <p className='fw-bold mt-3'>{locationProduct.address}</p>
                  {!!mediaBlock && mediaBlock}
                  <p>{String(address).toUpperCase()}</p>
                </InfoWindow>
              ) : (
                showingProductInfoWindow &&
                isShowingInfoBlock(producturn) &&
                locationProduct && (
                  <InfoWindow
                    marker={activeMarker}
                    onOpen={infoWindowOpened}
                    onClose={() => onInfoWindowClose()}
                    key={`infoWindow${idx}`}
                    id={`infoWindow${idx}`}
                    ref={(ref) => {
                      infoWindowRefs.current[idx] = ref;
                    }}
                    google={google}
                    map={(window as any).google.maps}
                    visible={true}
                  >
                    <div key={`windowContent${idx}`} id='infoWindowWrapper' className='info-popup'>
                      <div className={classNames('col p-0 w-100')}>
                        <div className='product-img-wrapper'>
                          <img
                            alt='Product'
                            className='product-img'
                            src={activeMarker.image + '&resolution=thumbnail'}
                          />
                        </div>
                      </div>

                      <div className='p-16 d-flex flex-column justify-content-start product-info'>
                        <h4 className='w-100 fw-bold font-16 mb-3'>
                          {locationProduct.format}
                          {locationProduct.attributeshortcuts}
                        </h4>

                        <div className='w-100 d-flex flex-row justify-content-start align-items-center'>
                          <div className='badge bg-light product-code'>
                            <span>ID: {activeMarker.nummer}</span>
                          </div>

                          {isMarkerColorGrey && (
                            <div className='d-flex justify-content-start align-items-center ms-4 availability'>
                              <span className='unavailable-checker ps-0'>
                                <RedX />
                              </span>
                              <span className='ps-0 font-12 font-300'>
                                {intl.formatMessage({
                                  id: 'ec.journey02.unavailable',
                                  defaultMessage: 'Nicht verfügbar',
                                })}
                              </span>
                            </div>
                          )}

                          {locations[keyAddress].state === 0 && !isMarkerColorGrey && (
                            <div className='d-flex justify-content-start align-items-center ms-3 availability'>
                              <span className='green-checker ps-0'>
                                <Check />
                              </span>

                              <span className='ps-0 font-12 font-300'>
                                {intl.formatMessage({
                                  id: 'ec.journey02.available',
                                  defaultMessage: 'Verfügbar',
                                })}
                              </span>
                            </div>
                          )}
                        </div>
                        <p className='font-500 font-14 mt-3 address'>{activeMarker.title}</p>
                      </div>
                    </div>
                  </InfoWindow>
                )
              ),

              isExistPolygon && ((showingPolygon === producturn && !modalMode) || modalMode) && (
                <Polygon
                  paths={paths}
                  key={`Polygon${idx}`}
                  strokeColor={modalMode ? markerColor : MarkerColors.Yellow}
                  strokeWeight={2}
                  fillColor={modalMode ? markerColor : MarkerColors.Yellow}
                  fillOpacity={0.2}
                />
              ),
            ]
          );
        })}

        {/* J2 step 1 */}
        {!(overviewJ2 || overviewJ1) &&
          Object.keys(locations).map((keyAddress, idx) => {
            return locations[keyAddress].types.includes('locality') ? null : (
              <Circle
                key={keyAddress + 'circle'}
                ref={(ref) => {
                  circleRefs.current[idx] = ref;
                }}
                radius={locations[keyAddress].circle.radius}
                center={locations[keyAddress].lngLtd}
                editable={true}
                draggable
                strokeColor='transparent'
                strokeOpacity={0}
                strokeWeight={5}
                fillColor='#FF0000'
                fillOpacity={0.2}
                onRadiusChanged={() => {
                  let radius, lat, lng;

                  if (circleRefs && circleRefs[idx] && circleRefs[idx].circle) {
                    radius = circleRefs[idx].circle.radius;
                    lat = circleRefs[idx].circle.center.lat();
                    lng = circleRefs[idx].circle.center.lng();
                  }

                  updateLocationRadius(locations[keyAddress], lat, lng, radius);
                }}
                onDragend={(props, rad, res) => {
                  const {
                    latLng: { lat, lng },
                  } = res;
                  const { radius } = rad;

                  updateLocationRadius(locations[keyAddress], lat(), lng(), radius);
                }}
                setCircle={(circle) => setCircles({ ...circles, [keyAddress]: circle })}
              />
            );
          })}

        {/* J2 Step6 map view with circles */}
        {overviewJ2 &&
          Object.keys(criteriaLocations).map((keyAddress, idx) => {
            return criteriaLocations[keyAddress] && criteriaLocations[keyAddress].types.includes('locality')
              ? null
              : [
                  criteriaLocations[keyAddress] && (
                    <Circle
                      key={keyAddress + 'circle'}
                      ref={(ref) => {
                        circleRefs.current[idx] = ref;
                      }}
                      radius={criteriaLocations[keyAddress].circle.radius}
                      center={criteriaLocations[keyAddress].lngLtd}
                      strokeColor='transparent'
                      strokeOpacity={0}
                      strokeWeight={5}
                      fillColor='#FF0000'
                      fillOpacity={0.2}
                      setCircle={(circle) => {
                        setCircles({ ...circles, [keyAddress]: circle });
                      }}
                    />
                  ),
                  circles[keyAddress] && criteriaLocations[keyAddress] && (
                    <Marker
                      onClick={onMarkerClick}
                      key={keyAddress + 'marker'}
                      idx={idx + 1000000}
                      title={criteriaLocations[keyAddress].address}
                      name={criteriaLocations[keyAddress].address}
                      circle={circles[keyAddress]}
                      position={{
                        lat: criteriaLocations[keyAddress].latitude,
                        lng: criteriaLocations[keyAddress].longitude,
                      }}
                    />
                  ),
                ];
          })}
        {cityBorders &&
          cityBorders.map((pol, idx) => (
            <Polygon
              paths={pol}
              key={`PolygonCity${idx}`}
              strokeColor={MarkerColors.Green}
              strokeWeight={2}
              fillColor={MarkerColors.Green}
              fillOpacity={0.2}
            />
          ))}
      </Map>
    </div>
  );
};

export default GoogleApiWrapper({ apiKey: placesAPIKey })(injectIntl(MapContainer));
